<template>
  <div class="multisearch">
    <v-menu
      v-model="isMenuVisible"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      bottom
      left
      content-class="multisearch__menu"
    >
      <template v-slot:activator="{ on }">
        <div v-on="on">
          <template v-if="selectedSearchParams.length > 0">
            <v-chip
              v-for="(selectedSearchParam, index) in selectedSearchParams"
              :key="index"
              close
              class="multisearch__chip mr-2"
              @click:close="removeParam(index)"
            >
              <span class="multisearch__chip-label">{{ selectedSearchParam.name }}:</span>
              <span
                v-for="(feature, index) in selectedSearchParam.features"
                :key="index"
                :class="{ 'multisearch__chip-last-value': selectedSearchParam.features.length > 1 }"
              >
                {{ feature.value }}
              </span>
            </v-chip>
          </template>

          <template v-else>
            <v-icon class="mr-3 mb-1" color="blue">mdi-filter-variant-plus</v-icon>
            <span class="multisearch__placeholder">
              Click to view filters
            </span>
          </template>
        </div>
      </template>

      <div class="multisearch__select">
        <ul v-if="!isParamSelected" class="multisearch__list">
          <li
            v-for="(param, index) in availableSearchParams"
            :key="index"
            class="multisearch__element"
            @click="selectParam(param)"
          >
            {{ param.name }}
          </li>
        </ul>

        <div v-show="isParamSelected">
          <v-form
            ref="multiselectSearchForm"
            v-model="isFormValid"
            class="multisearch__form"
            @submit.prevent="addParam"
          >
            <div class="multisearch__form-header">
              <v-icon size="24" color="blue" @click="abortSelect">mdi-close</v-icon>
            </div>

            <div class="multisearch__form-body">
              <label class="multisearch__input-label">{{ selectedParam.name }}</label>
              <div
                v-for="(feature, index) in selectedParam.features"
                :key="index"
                class="multisearch__form-body-row"
              >
                <v-autocomplete
                  v-if="feature.input_type === 'dropdown'"
                  v-model="selectedParam.features[index].value"
                  :rules="[rules.required]"
                  :items="feature.values"
                  solo
                  dense
                  item-color="blue"
                  placeholder="Select value"
                  class="multisearch__select-menu"
                />

                <v-combobox
                  v-else-if="feature.input_type === 'autocomplete'"
                  v-model="selectedParam.features[index].value"
                  :rules="[rules.required]"
                  :items="feature.values"
                  solo
                  dense
                  placeholder="Type or select value"
                  class="multisearch__select-menu"
                ></v-combobox>

                <v-text-field
                  v-else-if="feature.input_type === 'text'"
                  ref="paramNameField"
                  v-model="selectedParam.features[index].value"
                  :rules="[rules.required]"
                  class="multisearch__input"
                  label="Solo"
                  placeholder="Enter value"
                  solo
                  dense
                ></v-text-field>

                <v-menu
                  v-else-if="feature.input_type === 'date'"
                  ref="menuDateFrom"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                >
                  <template v-slot:activator="{ on }">
                    <div class="form-field--date-wrapper" v-on="on">
                      <v-text-field
                        v-model="selectedParam.features[index].value"
                        :rules="[rules.required]"
                        class="multisearch__input"
                        label="Solo"
                        placeholder="Click to pick the date"
                        solo
                        dense
                        readonly
                        v-on="on"
                      ></v-text-field>
                    </div>
                  </template>
                  <v-date-picker
                    v-model="selectedParam.features[index].value"
                    no-title
                    scrollable
                    @input="$refs.menuDateFrom[index].save(true)"
                  ></v-date-picker>
                </v-menu>
              </div>
            </div>

            <div class="multisearch__form-footer">
              <v-btn class="multisearch__btn" color="blue" dark type="submit">
                Search
              </v-btn>
            </div>
          </v-form>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import validationRules from '@/helpers/validators';

const rules = validationRules;

export default {
  props: {
    features: {
      type: Array,
      required: true,
    },

    selectedFeatures: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      rules,
      isMenuVisible: false,
      isParamSelected: false,
      isFormValid: true,
      selectedParam: {},
      selectedSearchParams: [],
    };
  },

  computed: {
    availableSearchParams() {
      const searchParams = this.features.filter((param) => {
        const isSelected = this.selectedSearchParams
          .some((searchParam) => searchParam.slug === param.slug);

        return !(isSelected && !param.multiselect);
      });

      return searchParams;
    },
  },

  created() {
    this.selectedSearchParams = this.selectedFeatures || [];
  },

  methods: {
    selectParam(param) {
      this.selectedParam = cloneDeep(param);
      this.$refs.multiselectSearchForm.reset();
      this.isParamSelected = true;
    },

    abortSelect() {
      this.isParamSelected = false;
      this.selectedParam = {};
    },

    addParam() {
      this.$refs.multiselectSearchForm.validate();
      if (this.isFormValid) {
        const newParam = cloneDeep(this.selectedParam);

        this.selectedSearchParams.push(newParam);

        this.$emit('search', this.selectedSearchParams);
        this.abortSelect();
      }
    },

    removeParam(index) {
      this.selectedSearchParams.splice(index, 1);
      this.$emit('search', this.selectedSearchParams);
    },
  },
};
</script>


<style lang="scss">
.multisearch {
  background: #fff;
  min-height: 44px;
  padding: 6px 16px;
  border-radius: 6px;
  border: 1px solid $color--secondary;
  margin-bottom: 16px;
  cursor: pointer;

  &__placeholder {
    padding: 4px 0;
    color: #bbb;
    display: inline-block;
  }

  &__select {
    width: 245px;
    background-color: #fff;;
  }

  &__list {
    list-style-type: none;
    padding-left: 0px !important;
  }

  &__element {
    color: gray;
    height: 30px;
    padding: 5px 20px;
    font-size: 14px;
    cursor: pointer;

    &:hover {
      background: #dfdede;
    }

    &:first-child {
      padding-top: 10px;
      height: 35px;
    }
    &:last-child {
      padding-bottom: 10px;
      height: 35px;
    }
  }

  &__chip-label {
    margin-right: 6px;
  }
  &__chip-last-value:not(:last-of-type) {
    margin-right: 6px;
  }

  &__form {
    min-height: 164px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  &__form-header {
    padding: 10px 10px 0 10px;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    cursor: default;
  }
  &__form-body {
    padding: 6px 12px 0;
  }
  &__input-label {
    font-size: 14px;
    color: gray;
  }
  &__form-footer {
    text-align: right;
    padding: 0 12px 10px;
  }
  &__btn {
    font-size: 14px;
    width: 100%;
    color: white;
  }

  .v-chip {
    background: $color--blue !important;
    color: #fff;
    border-radius: 6px;

    &__content {
      color: #fff;
    }

    .v-icon.mdi-close-circle {
      color: #fff;

      &::before {
        content: '\F156' !important;
      }
    }
  }
}

.multisearch__menu.v-menu__content {
  min-width: unset !important;
  // max-height: 164px;
  border-radius: 10px;
  background: #fff;
}
.multisearch__select-menu {
  .v-list-item {
    min-height: 40px;
    .v-list-item__content {
      padding: 8px 0;
      .v-list-item__title {
        font-size: 14px;
      }
    }
  }
}
</style>
